import { Box } from '@chakra-ui/react';

import {
  type BackToSchool2026SaleVariantConfig,
  BTS_2026_SALE_UNDERLINE_IMAGE,
} from '~/features/promotions/constants/back-to-school-2026-sale';

/**
 * The art is 318x20, so at `backgroundSize: 100% auto` it draws ~0.063x the phrase width. The
 * phrase runs ~5.8em at every surface's type scale, putting the art at ~0.37em tall. The padding
 * makes room for it; the matching negative margin keeps it out of the line box, since Figma draws
 * it as an absolutely positioned layer that overflows the heading.
 */
const UNDERLINE_RESERVED_SPACE = '0.4em';

/** Draws the underline art behind `phrase` wherever it appears in `text`; plain text if it does not. */
export function BackToSchool2026UnderlinedPhrase({ phrase, text }: { phrase: string; text: string }) {
  const phraseStart = text.lastIndexOf(phrase);

  if (phraseStart < 0) {
    return <>{text}</>;
  }

  return (
    <>
      {text.slice(0, phraseStart)}
      <Box
        as='span'
        backgroundImage={`url('${BTS_2026_SALE_UNDERLINE_IMAGE}')`}
        backgroundPosition='left bottom'
        backgroundRepeat='no-repeat'
        backgroundSize='100% auto'
        display='inline-block'
        marginBottom={`-${UNDERLINE_RESERVED_SPACE}`}
        paddingBottom={UNDERLINE_RESERVED_SPACE}
      >
        {phrase}
      </Box>
      {text.slice(phraseStart + phrase.length)}
    </>
  );
}

/**
 * Back to School headline: an accented line 1 stacked over a white line 2, with the hand-drawn
 * underline behind the price phrase (Figma 1856:2052 homepage, 1856:2182 experience ATF). Returned
 * as heading children so each surface keeps its own type scale and title color.
 */
export function BackToSchool2026HeadlineLines({ config }: { config: BackToSchool2026SaleVariantConfig }) {
  return (
    <>
      <Box as='span' color={config.headlineAccentColor}>
        {config.headlineLine1}
      </Box>
      <br />
      <BackToSchool2026UnderlinedPhrase phrase={config.headlineUnderlinePhrase} text={config.headlineLine2} />
    </>
  );
}
